import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
    saveAppPageGroup,
    removePageFromGroup,
    type AppPageGroup,
} from '@/services/api/app-page-groups';
import { useToast } from '@/hooks/use-toast';

/**
 * Página priorizada num app em que nenhum perfil dela tem token vivo.
 *
 * A trava do grupo só pega se existir token (page_id, app_id): sem ele o
 * envio ignora a prioridade e escolhe outro app. As duas saídas são mover a
 * prioridade para um app onde a página TEM token, ou tirar a prioridade.
 *
 * Mover é um PUT no grupo do app de destino com a lista dele + esta página;
 * a chave única (page_id, user_id) tira a página do grupo antigo na mesma
 * escrita. Por isso a lista precisa vir inteira — mandar só esta página
 * esvaziaria o grupo de destino.
 */

export interface PaginaSemToken {
    pageId: string;
    pageName: string;
    donoId: number;
    appId: number;
    appName: string | null;
}

export interface PerfilDaPagina {
    meta_user_id: number;
    meta_user_name: string | null;
    app_id?: number;
    app_name: string | null;
    token_status: string | null;
}

interface Props {
    pagina: PaginaSemToken | null;
    perfis: PerfilDaPagina[];
    grupos: AppPageGroup[];
    onClose: () => void;
}

export default function AppSemTokenFixModal({ pagina, perfis, grupos, onClose }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [salvando, setSalvando] = useState<string | null>(null);

    if (!pagina) return null;

    // App com token vivo nesta página, um por app (vários perfis podem ter o mesmo).
    const appsComToken = new Map<number, { appName: string | null; perfis: string[] }>();
    for (const p of perfis) {
        if (p.app_id == null || p.app_id === pagina.appId) continue;
        if (p.token_status && p.token_status !== 'ok') continue;
        const atual = appsComToken.get(p.app_id) ?? { appName: p.app_name, perfis: [] };
        atual.perfis.push(p.meta_user_name ?? `Perfil #${p.meta_user_id}`);
        appsComToken.set(p.app_id, atual);
    }

    const concluir = (titulo: string, descricao: string) => {
        queryClient.invalidateQueries({ queryKey: ['app-page-groups'] });
        toast({ title: titulo, description: descricao });
        onClose();
    };

    const falhar = (e: any) => {
        const detail = e?.response?.data?.detail;
        toast({
            variant: 'destructive',
            title: 'Erro ao salvar',
            description: typeof detail === 'string' ? detail : detail?.message ?? 'Tente de novo.',
        });
    };

    const priorizar = async (appId: number, appName: string | null) => {
        setSalvando(`app-${appId}`);
        try {
            const destino = grupos.find(g => g.app_id === appId && (g.user_id == null || g.user_id === pagina.donoId));
            const pageIds = [...(destino?.pages.map(p => p.page_id) ?? []), pagina.pageId];
            await saveAppPageGroup({ app_id: appId, page_ids: [...new Set(pageIds)], userId: pagina.donoId });
            concluir('Prioridade ajustada', `${pagina.pageName} passa a enviar pelo ${appName ?? `App #${appId}`}.`);
        } catch (e) {
            falhar(e);
        } finally {
            setSalvando(null);
        }
    };

    const tirarPrioridade = async () => {
        setSalvando('remover');
        try {
            await removePageFromGroup(pagina.pageId, pagina.donoId);
            concluir('Prioridade removida', `${pagina.pageName} volta a usar o app menos carregado.`);
        } catch (e) {
            falhar(e);
        } finally {
            setSalvando(null);
        }
    };

    const nomeApp = pagina.appName ?? `App #${pagina.appId}`;

    return (
        <Dialog open={!!pagina} onOpenChange={o => { if (!o && !salvando) onClose(); }}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Ajustar app priorizado</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                            <strong>{pagina.pageName}</strong> está priorizada no app{' '}
                            <strong>{nomeApp}</strong>, mas nenhum perfil dela tem token ativo nesse app.
                            A prioridade não vale e o envio usa outro app.
                        </span>
                    </div>

                    {appsComToken.size > 0 ? (
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">Priorizar um app em que a página tem token:</p>
                            {[...appsComToken.entries()].map(([appId, a]) => (
                                <div key={appId} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-sm text-gray-800 truncate">{a.appName ?? `App #${appId}`}</div>
                                        <div className="text-[11px] text-gray-400 truncate">{a.perfis.join(', ')}</div>
                                    </div>
                                    <Button size="sm" onClick={() => priorizar(appId, a.appName)} disabled={!!salvando}>
                                        {salvando === `app-${appId}` && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                        Priorizar
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
                            A página não tem token ativo em nenhum outro app. Para manter a prioridade em{' '}
                            <strong>{nomeApp}</strong>, vincule de novo um perfil da página por esse app
                            (botão <strong>Vincular Página</strong>). Ou tire a prioridade abaixo.
                        </div>
                    )}
                </div>

                <DialogFooter className="sm:justify-between gap-2">
                    <Button variant="outline" onClick={tirarPrioridade} disabled={!!salvando}>
                        {salvando === 'remover' && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Tirar prioridade (automático)
                    </Button>
                    <Button variant="ghost" onClick={onClose} disabled={!!salvando}>Fechar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
